import { darken, rgbToHex } from "@mui/material/styles";
import { hexToRgb, muiHueFromRgbColor, muiShadesFromHue, rgbToCSS } from "common/utilities/colors/colors";
import type { Color } from "common/utilities/colors/colors.types";
import { randomBetween } from "common/utilities/core/number";
import {
  type SequenceFeature,
  type SequenceFeatureColor,
  SequenceFeatureTypeColorSetting,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { FeatureId, FeaturesColors, FeatureTypesColor } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { buildFeatureId } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser.ts";
import ColorValues from "values.js";

import type { Defaults, FeatureTypesColorSetting } from "./defaults";

/**
  Features colors to work as follow:

  - colors: only used colors provided by the database or MUI palette
    - go in circle if there are more colors required than available

  - feature name (i.e. LCDR1):
    - might have a hardcoded color (in featuresColors.ts)
      - or inherit the logic from feature types (below)

  - feature types (i.e. CDR):
    - color:
      - might be returned by the API
      - or hardcoded (in featuresColors.ts)
      - or set from a pre-defined palette
    - color setting (identical, distinct, shade):
      - might be hardcoded (in featuresColors.ts)
      - defaults to identical
      - is not influenced by a color been returned by the API (user can toggle setting afterwards)
    - logic:
      - identical: use feature type color
      - distinct: use feature name color (when hardcoded in featuresColors.ts)
        - or same shade from multiple hues (go in circle)
      - shade: use feature type color related hue shades
        - or (if not part of hue) use getShade method
*/

export function getFeatureTypeColorSetting(
  featureType: string,
  userFeatureTypeColor: SequenceFeatureColor | undefined,
  defaultFeatureTypesColorSetting: FeatureTypesColorSetting,
) {
  return (
    userFeatureTypeColor?.setting || defaultFeatureTypesColorSetting[featureType] || SequenceFeatureTypeColorSetting.I
  );
}

/** 'key' is made of the color and index */
const cachedShades: Record<string, Color> = {};

export function getShade(color: Color, index: number) {
  const id = `${color.join("-")}-${index}`;
  if (cachedShades[id]) return cachedShades[id];

  const rgbColorAsCSS = rgbToCSS(color);
  let colorValue = new ColorValues(rgbColorAsCSS);

  const hue = muiHueFromRgbColor(color);

  // If the color is part of a MUI hue, use shades of that hue
  if (hue) {
    const shadesColor = muiShadesFromHue(hue);
    return hexToRgb(shadesColor[index % shadesColor.length]) as Color;
  }

  // Otherwise generate a shade color on the fly
  // Some colors are too brights and can't get alternative tint so we darken them
  if (colorValue.getBrightness() > 53) {
    colorValue = new ColorValues(darken(rgbToHex(rgbColorAsCSS), 0.6));
  }

  /** Alternate tint between brighter and darker to get a clearer color list */
  const tintValues = [80, 40, 60, 30, 70, 50, 90, 20];
  const tintValue = tintValues[index - 1] || randomBetween(20, 80);
  const shadeColor = colorValue.tint(tintValue).rgb as Color;

  cachedShades[id] = shadeColor;
  return shadeColor;
}

export interface PartialFeature {
  label: string;
  featureType: string;
  featureTypeId: number;
}

/**
 * Generate colors for features based on provided colors by the API
 * and feature type's color setting.
 * Default feature names will be enforced when color setting is set to 'distinct'.
 */
export function generateFeaturesColors(
  features: SequenceFeature[],
  _existingFeatureTypeColor: FeatureTypesColor | undefined,
  _userColors: SequenceFeatureColor[],
  _defaults: Defaults,
) {
  const featureTypesColor: FeatureTypesColor = {};

  const featuresColors = features.reduce<FeaturesColors>((acc, feature) => {
    const featureId: FeatureId = buildFeatureId(feature);
    if (acc[featureId]) return acc;

    const { type } = feature;

    if (type === "gene") {
      acc[featureId] = { color: [155, 215, 196], source: "hardcoded" };
      featureTypesColor[type] = { color: [155, 215, 196], source: "hardcoded" }; // for feature table category
    } else if (type === "protein") {
      acc[featureId] = { color: [235, 195, 205], source: "hardcoded" };
      featureTypesColor[type] = { color: [235, 195, 205], source: "hardcoded" };
    } else {
      acc[featureId] = { color: [248, 172, 148], source: "hardcoded" };
      featureTypesColor[type] = { color: [248, 172, 148], source: "hardcoded" };
    }

    return acc;
  }, {});

  return { featuresColors, featureTypesColor };
}
